import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Play, Pause } from 'lucide-react';

export default function OpenMediaViewer({ openMedia, onClose }) {
  const videoRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoTime, setVideoTime] = useState({ current: 0, duration: 0 });
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubValue, setScrubValue] = useState(0);
  const wasPlayingRef = useRef(false);

  const isVideo = openMedia?.type === 'video';

  const formatTime = (s) => {
    if (!isFinite(s) || s == null) return '0:00';
    s = Math.floor(s);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const mm = h ? String(m).padStart(2, '0') : String(m);
    const ss = String(sec).padStart(2, '0');
    return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  };

  const togglePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  };

  const handleScrubStart = () => {
    const v = videoRef.current;
    if (!v) return;
    wasPlayingRef.current = !v.paused;
    v.pause();
    setIsScrubbing(true);
  };

  const handleScrubChange = (e) => {
    const v = videoRef.current;
    if (!v) return;
    const val = parseFloat(e.target.value || '0');
    setScrubValue(val);
    try { v.currentTime = val; } catch {}
    setVideoTime((t) => ({ ...t, current: val }));
  };

  const handleScrubEnd = () => {
    const v = videoRef.current;
    if (!v) return;
    setIsScrubbing(false);
    try { v.currentTime = scrubValue; } catch {}
    if (wasPlayingRef.current) {
      v.play().catch(() => {});
    }
  };

  useEffect(() => {
    if (!openMedia || !isVideo) return;
    const v = videoRef.current;
    if (!v) return;

    const onLoaded = () => setVideoTime((t) => ({ ...t, duration: v.duration || 0 }));
    const onTime = () => setVideoTime({ current: v.currentTime || 0, duration: v.duration || 0 });
    const onPlay = () => setIsVideoPlaying(true);
    const onPause = () => setIsVideoPlaying(false);

    v.addEventListener('loadedmetadata', onLoaded);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);

    v.play().catch(() => {});

    return () => {
      v.removeEventListener('loadedmetadata', onLoaded);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      try { v.pause(); } catch {}
      setIsVideoPlaying(false);
      setVideoTime({ current: 0, duration: 0 });
      setIsScrubbing(false);
    };
  }, [openMedia, isVideo]);

  if (!openMedia) return null;

  const progressPercent = videoTime.duration
    ? ((isScrubbing ? scrubValue : videoTime.current) / videoTime.duration) * 100
    : 0;

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
      <button
        className="absolute top-12 left-4 p-2 z-50"
        onClick={() => {
          if (videoRef.current) {
            try { videoRef.current.pause(); } catch {}
          }
          onClose?.();
        }}
      >
        <X className="w-8 h-8 text-white" />
      </button>

      {openMedia.type === 'photo' ? (
        <img
          src={openMedia.src}
          alt="Full view"
          className="max-w-full object-contain"
          style={{ maxHeight: 'calc(100vh - 196px)' }}
        />
      ) : (
        <div className="relative w-full h-full flex items-center justify-center">
          <video
            ref={videoRef}
            src={openMedia.src}
            className="max-w-full object-contain cursor-pointer"
            style={{ maxHeight: 'calc(100vh - 196px)' }}
            playsInline
            autoPlay
            onClick={(e) => { e.stopPropagation(); togglePlayPause(); }}
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[90%] max-w-xl px-4 py-3">
            <input
              type="range"
              min={0}
              max={videoTime.duration || 0}
              step={0.1}
              value={isScrubbing ? scrubValue : videoTime.current}
              onChange={handleScrubChange}
              onInput={handleScrubChange}
              onMouseDown={handleScrubStart}
              onTouchStart={handleScrubStart}
              onMouseUp={handleScrubEnd}
              onTouchEnd={handleScrubEnd}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-white"
              style={{
                background: `linear-gradient(to right, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.9) ${progressPercent}%, rgba(255,255,255,0.2) ${progressPercent}%, rgba(255,255,255,0.2) 100%)`
              }}
            />
            <div className="mt-2 flex items-center justify-between">
              <button
                onClick={togglePlayPause}
                className="text-white p-1 rounded-full hover:bg-white/10"
              >
                {isVideoPlaying ? (
                  <Pause className="w-6 h-6" />
                ) : (
                  <Play className="w-6 h-6" />
                )}
              </button>
              <span className="text-white text-sm tracking-wide">
                {formatTime(isScrubbing ? scrubValue : videoTime.current)} / {formatTime(videoTime.duration)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
